import React, { useState, useEffect } from "react";
import MenuIcon from "@mui/icons-material/Menu";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { fetchUserAttributes } from "aws-amplify/auth";
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Typography,
  Stack,
  Skeleton,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import { UiShellDrawer } from "./UiShellDrawer";
import { GET_ORGANIZATION_NAME } from "../../constants/api";
import { drawerWidth } from "./UiShell.styles";
import axios from "axios";
import { useDispatch, useSelector } from "react-redux";
import {
  setOrgName,
  setOrgId,
  setOrgNameFetchedAt,
} from "../../store/reducers/user.reducer";
import { useGetAuthToken } from "../../hooks/useGetAuthToken";
import { clearSessionExpiredFlag } from "../../utils/axiosAuthInterceptor";
import { RootState } from "../../store/store";
import { Snackbar } from "@mui/material";
import { getAuthConfig } from "../../utils/authHeaders";

interface UiShellAuthProps {
  children: React.ReactNode;
}

export const UiShellAuth: React.FC<UiShellAuthProps> = ({ children }) => {
  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("sm"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { user } = useAuthenticator((context) => [context.user]);
  const [orgNameIsLoading, setOrgNameIsLoading] = useState(false);
  const [forbiddenMessage, setForbiddenMessage] = useState<string | null>(null);
  const dispatch = useDispatch();
  const { token, tokenError, tokenIsLoading } = useGetAuthToken();
  const { orgName, orgNameFetchedAt } = useSelector(
    (state: RootState) => state.user
  );
  const isOrgNameStale = orgNameFetchedAt
    ? Date.now() - orgNameFetchedAt > 5 * 60 * 1000
    : true;

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  useEffect(() => {
    if (user?.signInDetails?.loginId) {
      clearSessionExpiredFlag();
    }
  }, [user?.signInDetails?.loginId]);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<string>).detail;
      setForbiddenMessage(detail);
    };
    window.addEventListener("auth:forbidden", handler);
    return () => {
      window.removeEventListener("auth:forbidden", handler);
    };
  }, []);

  useEffect(() => {
    async function handleFetchUserAttributes() {
      try {
        const userAttributes = await fetchUserAttributes();
        const orgIdAttribute = userAttributes
          ? userAttributes["custom:orgId"]
          : null;
        dispatch(setOrgId(orgIdAttribute || null));
      } catch (error) {
        console.log(error);
      }
    }

    handleFetchUserAttributes();
  }, []);

  useEffect(() => {
    async function fetchOrgName() {
      if (token && (!orgName || isOrgNameStale)) {
        setOrgNameIsLoading(true);
        try {
          const url = `${GET_ORGANIZATION_NAME}`;
          const response = await axios.get(url, getAuthConfig(token));
          dispatch(setOrgName(response.data.name));
          dispatch(setOrgNameFetchedAt(Date.now()));
        } catch (error) {
          if (error instanceof Error) {
            console.log(error.message);
          } else {
            console.log(String(error));
          }
        } finally {
          setOrgNameIsLoading(false);
        }
      }
    }

    fetchOrgName();
  }, [token, orgName, isOrgNameStale]);

  if (tokenError) return <p>{`Error: ${tokenError}`}</p>;

  return (
    <>
      <Box sx={{ display: "flex" }}>
        <AppBar
          position="fixed"
          elevation={0}
          color="transparent"
          sx={{
            width: { sm: `calc(100% - ${drawerWidth}px)` },
            ml: { sm: `${drawerWidth}px` },
            backgroundColor: theme.palette.background.default,
            borderBottom: theme.palette.divider,
          }}
        >
          <Toolbar>
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: "none" } }}
            >
              <MenuIcon />
            </IconButton>
            <Box display="flex" justifyContent={`space-between`} width={`100%`}>
              {orgNameIsLoading || !orgName?.trim() ? (
                <Skeleton
                  variant="text"
                  sx={{ fontSize: "1rem", width: 160 }}
                />
              ) : (
                <Typography
                  variant="h6"
                  component="h1"
                  textTransform="uppercase"
                  color="primary"
                >
                  {orgName}
                </Typography>
              )}
              <Stack direction={`row`} spacing={2}>
                {!user.signInDetails?.loginId ? (
                  <Skeleton variant="text" sx={{ fontSize: "1rem" }} />
                ) : (
                  <Typography>{`${user.signInDetails?.loginId}`}</Typography>
                )}
              </Stack>
            </Box>
          </Toolbar>
        </AppBar>
        <Box
          component="nav"
          sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
          aria-label="mailbox folders"
        >
          <UiShellDrawer
            mobileOpen={mobileOpen}
            handleDrawerClose={handleDrawerClose}
            handleDrawerTransitionEnd={handleDrawerTransitionEnd}
          />
        </Box>
        <Box
          component="main"
          pt={`128px`}
          width="100%"
          display="flex"
          flexDirection="column"
          minHeight="100vh"
        >
          <Box flex="1 0 auto">
            <Box px={5} pb={5}>
              {children}
            </Box>
          </Box>
        </Box>
      </Box>
      <Snackbar
        open={!!forbiddenMessage}
        autoHideDuration={5000}
        onClose={() => setForbiddenMessage(null)}
        message={forbiddenMessage}
      />
    </>
  );
};
